import React, { useMemo } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ModuleLayout from '../../layout/ModuleLayout';
import { useTranslation } from 'react-i18next';
import {
    FaShoppingCart,
    FaCashRegister,
    FaFileContract,
    FaTruck,
    FaFileInvoice,
    FaUndoAlt,
    FaUserCircle,
    FaTruckMoving,
    FaList,
    FaMoneyCheck,
    FaChartLine,
    FaUser,
    FaBuilding
} from 'react-icons/fa';

// Import components
import SaleForm from '../Sales/SalesForm';
import DevisList from '../Devis/DevisList';
import BonCommandeList from '../Devis/BonCommandeList';
import DeliveryList from '../BonLivraison/DeliveryListe';
import InvoiceList from '../Factures/InvoiceListe/InvoiceList';
import NewInvoicePage from '../Factures/NewInvoicePage';
import CustomerReturnsList from '../RetourClient/CustomerReturnsList';
import CustomerReturnForm from '../RetourClient/CustomerReturnForm';
import CustomerReturnDetail from '../RetourClient/CustomerReturnDetail';
import ClientList from '../Client/ClientList';
import ClientForm from '../Client/ClientForm';
import TransporteurPage from '../Transporteur/TransporteurPage';
import CommercialList from '../Commercial/CommercialList';
import ReglementsList from '../ReglementClient/ReglementsList';
import ReglementForm from '../ReglementClient/ReglementForm';
import ClientEcheance from '../ReglementClient/ClientEcheance';
import VentesStatistiques from '../Dashboard/VentesStatistiques';
import MouvementSoldeClient from '../MouvementSolde/MouvementSoldeClient';
import MouvementSoldeChantier from '../MouvementSolde/MouvementSoldeChantier';
import CommissionPlanList from '../Commercial/CommissionPlanList';
import CommissionHistoryPage from '../Commercial/CommissionHistoryPage';
import CommercialProgressPage from '../Commercial/CommercialProgressPage';
import EtatClient from '../Client/EtatClient';
import LettragePage from '../ReglementClient/LettragePage';
import PaymentAllocation from '../ReglementClient/PaymentAllocation';

const SalesModule = () => {
    const { t } = useTranslation();
    const location = useLocation();

    const moduleConfig = {
        id: 'sales',
        title: t('modules.sales.title'),
        description: t('modules.sales.description'),
        icon: FaShoppingCart,
        color: '#17a2b8',
        menuItems: [
            { path: '/sales-module/quotes', icon: FaFileContract, label: t('nav.devis') },
            { path: '/sales-module/orders', icon: FaShoppingCart, label: t('nav.order') },
            { path: '/sales-module/delivery', icon: FaTruck, label: t('nav.livraison') },
            { path: '/sales-module/invoices', icon: FaFileInvoice, label: t('nav.invoice') },
            { path: '/sales-module/returns', icon: FaUndoAlt, label: t('nav.refund') },
            {
                label: t('nav.clients'),
                icon: FaUserCircle,
                subMenu: [
                    { path: '/sales-module/clients', icon: FaFileInvoice, label: t('nav.clients') },
                    { path: '/sales-module/clients-etat', icon: FaList, label: t('nav.clientEtat') }
                ]
            },
            { path: '/sales-module/transporters', icon: FaTruckMoving, label: t('nav.transporteurs') },
            {
                label: t('nav.commercials'),
                icon: FaUserCircle,
                subMenu: [
                    { path: '/sales-module/commercials', icon: FaFileInvoice, label: t('nav.commercials') },
                    { path: '/sales-module/commercials-progress', icon: FaList, label: t('nav.commercialsProgress') },
                ]
            },
            {
                label: t('modules.finance.title'),
                icon: FaMoneyCheck,
                subMenu: [
                    { path: '/sales-module/payments', icon: FaList, label: t('nav.listeReglements') },
                    { path: '/sales-module/payments/allocations', icon: FaList, label: t('nav.listeAllocations') },
                    { path: '/sales-module/client-deadlines', icon: FaMoneyCheck, label: t('nav.echeanceClient') },
                ]
            },
            {
                label: t('modules.reports.title'),
                icon: FaChartLine,
                subMenu: [
                    { path: '/sales-module/sales', icon: FaChartLine, label: t('nav.salesReports') },
                    { path: '/sales-module/customers', icon: FaUser, label: t('nav.customerReports') },
                ]
            },

        ]
    };

    // Generate breadcrumbs based on current route
    const breadcrumbs = useMemo(() => {
        const path = location.pathname;
        const breadcrumbList = [];

        if (path.includes('/clients')) {
            if (path.includes('/new')) {
                breadcrumbList.push(
                    { label: 'Clients', href: '/sales-module/clients' },
                    { label: 'Nouveau Client' }
                );
            } else if (path.includes('/edit')) {
                breadcrumbList.push(
                    { label: 'Clients', href: '/sales-module/clients' },
                    { label: 'Modifier Client' }
                );
            } else {
                breadcrumbList.push({ label: 'Clients' });
            }
        } else if (path.includes('/invoices')) {
            if (path.includes('/new')) {
                breadcrumbList.push(
                    { label: 'Factures', href: '/invoices' },
                    { label: 'Nouvelle Facture' }
                );
            } else {
                breadcrumbList.push({ label: 'Factures' });
            }
        } else if (path.includes('/returns')) {
            if (path.includes('/new')) {
                breadcrumbList.push(
                    { label: 'Retours', href: '/returns' },
                    { label: 'Nouveau Retour' }
                );
            } else {
                breadcrumbList.push({ label: 'Retours' });
            }
        } else if (path.includes('/quotes')) {
            breadcrumbList.push({ label: 'Devis' });
        } else if (path.includes('/orders')) {
            breadcrumbList.push({ label: 'Commandes' });
        } else if (path.includes('/delivery')) {
            breadcrumbList.push({ label: 'Livraisons' });
        } else if (path.includes('/transporters')) {
            breadcrumbList.push({ label: 'Transporteurs' });
        } else if (path.includes('/quotes')) {
            breadcrumbList.push({ label: 'Point de Vente' });
        }

        return breadcrumbList;
    }, [location.pathname]);

    return (
        <ModuleLayout moduleConfig={moduleConfig} breadcrumbs={breadcrumbs}>
            <Routes>
                <Route path="/" element={<Navigate to="/sales-module/quotes" replace />} />
                <Route path="/quotes" element={<DevisList />} />
                <Route path="/orders" element={<BonCommandeList />} />
                <Route path="/delivery" element={<DeliveryList />} />
                <Route path="/invoices" element={<InvoiceList />} />
                <Route path="/invoices/new" element={<NewInvoicePage />} />
                <Route path="/payments" element={<ReglementsList />} />
                <Route path="/payments/allocations" element={<PaymentAllocation />} />
                <Route path="/payments/edit" element={<ReglementForm />} />
                <Route path="/payments/new" element={<ReglementForm />} />
                <Route path="/client-deadlines" element={<ClientEcheance />} />
                <Route path="/returns" element={<CustomerReturnsList />} />
                <Route path="/returns/new" element={<CustomerReturnForm />} />
                <Route path="/returns/:id" element={<CustomerReturnDetail />} />
                <Route path="/clients" element={<ClientList />} />
                <Route path="/clients-etat" element={<EtatClient />} />
                <Route path="/clients/new" element={<ClientForm />} />
                <Route path="/clients/:id/edit" element={<ClientForm />} />
                <Route path="/transporters" element={<TransporteurPage />} />
                <Route path="/commercials" element={<CommercialList />} />
                <Route path="/commercials-progress" element={<CommercialProgressPage />} />
                <Route path="/sales" element={<VentesStatistiques />} />
                <Route path="/customers" element={<MouvementSoldeClient />} />
                <Route path="/chantier" element={<MouvementSoldeChantier />} />
                <Route path="/reglements/:id/lettrage" element={<LettragePage />} />
            </Routes>
        </ModuleLayout>
    );
};

export default SalesModule;
